import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/sub-comp/Navbar";
import Footer from "../components/sub-comp/MainFooter";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import cmsApi from "../services/cmsApi";
import { getImageUrl, FALLBACK_IMAGE_SVG } from "../utils/getImageUrl";

const AllEvents = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // Fetch events from CMS API
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const fetchEvents = async () => {
      try {
        setLoading(true);
        setLoadError("");
        // Fetch all events from CMS
        const result = await cmsApi.getEvents({ limit: 100 });
        
        if (result.success && result.events && result.events.length > 0) {
          // Transform CMS events to match expected format
          const transformedEvents = result.events.map(event => ({
            id: event.id,
            slug: event.slug || event.id,
            title: event.title,
            description: event.description || '',
            fullDescription: event.content || event.description || '',
            image: event.image || '',
            date: event.date || '',
            dateISO: event.dateISO || '',
            category: event.category || 'Other',
            location: event.location || '',
            time: event.time || '',
            featured: event.featured || false,
            status: event.status || 'upcoming'
          }));
          setEvents(transformedEvents);
        } else {
          setEvents([]);
          setLoadError("No events available right now.");
        }
      } catch (error) {
        setEvents([]);
        setLoadError("Unable to load events right now.");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const filteredEvents = events.filter(event => {
    const matchesCategory = selectedCategory === "all" || 
                            event.category === selectedCategory ||
                            event.category?.toLowerCase() === selectedCategory.toLowerCase();
    const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredEvents = events.filter(event => event.featured);
  const categories = [{ id: "all", name: "All Events" }].concat(
    [...new Set(events.map((event) => event.category).filter(Boolean))].map((category) => ({
      id: category,
      name: category,
    }))
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="mid-desktop-page">
        <div className="container mx-auto px-4 py-20 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-custom-pink border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading events...</p>
        </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="mid-desktop-page">
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-custom-pink to-purple-800 text-white">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">
            Campus <span className="text-gold">Events</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
            Stay updated with all the exciting events happening at JRIIT
          </p>
        </div>
      </section>

      {/* Featured Events Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-custom-pink mb-4">
              Featured Events
            </h2>
            <div className="w-32 h-1 bg-gold mx-auto rounded-full"></div>
            {!loading && loadError && <p className="mt-4 text-sm text-red-500">{loadError}</p>}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredEvents.map((event) => (
              <div
                key={event.id}
                className="group bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:scale-105"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={getImageUrl(event.image)}
                    alt={event.title}
                    className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-110"
                    onError={(e) => {
e.currentTarget.src = FALLBACK_IMAGE_SVG;
                    }}
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-gold text-black text-sm font-bold rounded-full">
                      Featured
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-custom-pink text-white text-sm font-bold rounded-full">
                      {event.date}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                      {event.time}
                    </span>
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                      {event.location}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-custom-pink transition-colors duration-300">
                    {event.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {event.description}
                  </p>
                  
                  <Link
                    to={`/events/${event.slug || event.id}`}
                    className="inline-block bg-custom-pink text-white px-6 py-3 rounded-full font-semibold hover:bg-purple-700 transition-colors duration-300 transform hover:scale-105"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-8 bg-gray-100 border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search Bar */}
            <div className="relative w-full md:w-96">
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-custom-pink focus:border-transparent"
              />
              <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-full font-medium transition-all duration-300 ${
                    selectedCategory === category.id
                      ? "bg-custom-pink text-white shadow-lg transform scale-105"
                      : "bg-white text-gray-700 hover:bg-gray-200 hover:text-custom-pink border border-gray-200"
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* All Events Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              All Events ({filteredEvents.length})
            </h2>
            <div className="text-sm text-gray-600">
              Showing {filteredEvents.length} of {events.length} events
            </div>
          </div>

          {filteredEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredEvents.map((event) => (
                <div
                  key={event.id}
                  className="group bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:scale-105"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={getImageUrl(event.image)}
                      alt={event.title}
                      className="w-full h-48 object-cover transition-transform duration-700 group-hover:scale-110"
                      onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE_SVG; }}
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-custom-pink text-white text-sm font-bold rounded-full">
                        {event.date}
                      </span>
                    </div>
                    {event.featured && (
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1 bg-gold text-black text-sm font-bold rounded-full">
                          Featured
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                        {event.time}
                      </span>
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                        {event.location}
                      </span>
                    </div>
                    
                    <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-custom-pink transition-colors duration-300 line-clamp-2">
                      {event.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3 text-sm">
                      {event.description}
                    </p>
                    
                    <Link
                      to={`/events/${event.slug || event.id}`}
                      className="inline-block bg-custom-pink text-white px-5 py-2 rounded-full font-semibold hover:bg-purple-700 transition-colors duration-300 transform hover:scale-105 text-sm"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No events found</h3>
              <p className="text-gray-500">Try adjusting your search or filter criteria.</p>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-custom-pink to-purple-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Want to Stay Updated?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Subscribe to our newsletter and never miss an important campus event!
          </p>
          <Link to="/contact" className="inline-block bg-gold text-black px-8 py-3 rounded-full font-semibold hover:bg-yellow-400 transition-colors duration-300 transform hover:scale-105">
              Contact Us
            </Link>
        </div>
      </section>

      </div>

      <Footer />
      <FloatingSocialPanels />
    </div>
  );
};

export default AllEvents;
